import produce from "immer";
import find from "lodash/find";
import { v1 as uuid } from "uuid";
import { CustomizationState } from "../";
import { Configuration } from "../../../types/customization/configuration";
import { ContentEditablePicture, ContentPage } from "../../../types/customization/content";
import {
  TemplateEditablePictureDefinition,
  TemplatePageDefinition,
} from "../../../types/customization/template";
import { SelectionPhoto } from "../../../types/selection/selection";
import { sizeDmmToMm } from "../../../utils/customization/box";
import { maxPageReached } from "../../../utils/customization/configuration";
import { getCoverFilteredPages, getNoCoverDefinitions } from "../../../utils/customization/cover";
import { calcDefaultCropping } from "../../../utils/customization/cropping";
import {
  findEditablePictureDefinition,
  findPage,
  findPageDefinition,
} from "../../../utils/customization/finders";
import {
  addPageMutation,
  firstNotSetEditablePictureDefinition,
  firstNotSetEditablePictureDefinitionAndPageInPageList,
  generateNewPage,
  getNumberPagesToAdd,
  singleEditablePictureDefinition,
} from "../../../utils/customization/page";
import { getMaxPageReachedMessage } from "../../../utils/customMessages";
import { addNotificationRemovingSameOnes } from "../../../utils/notification";
import { addSelectionPhotoToSelection } from "../../../utils/selection/photo";

export interface AddPictureAction {
  avoidCover?: boolean;
  configuration: Configuration | undefined;
  editablePictureKey?: string;
  pageKey?: string;
  selectionPhoto: SelectionPhoto;
  tryToFillFirstEmptyPicture: boolean;
  type: "addPicture";
}

export function addPicture(
  state: CustomizationState,
  action: AddPictureAction
): CustomizationState {
  return produce(state, (draftState) => {
    doAddPicture(draftState, action);
  });
}

export function doAddPicture(draftState: CustomizationState, action: AddPictureAction): void {
  const {
    configuration,
    editablePictureKey,
    pageKey,
    selectionPhoto: candidateSelectionPhoto,
    tryToFillFirstEmptyPicture,
  } = action;

  let selectionPhoto: SelectionPhoto | undefined;
  if (candidateSelectionPhoto.fotomKey) {
    selectionPhoto = find(
      draftState.selection.photos,
      ({ fotomKey }) => !!fotomKey && fotomKey === candidateSelectionPhoto.fotomKey
    );
  }
  if (!selectionPhoto) {
    selectionPhoto = candidateSelectionPhoto;
  }

  let targetPage: ContentPage | undefined;
  let targetEditablePictureDefinition: TemplateEditablePictureDefinition | undefined;

  const lastPage =
    draftState.content.pages.length > 0
      ? draftState.content.pages[draftState.content.pages.length - 1]
      : undefined;
  if (draftState.content.pages.length > 0) {
    let pages = draftState.content.pages;
    if (action.avoidCover) {
      pages = getCoverFilteredPages(
        pages,
        getNoCoverDefinitions(draftState.template.pageDefinitions)
      );
    }
    const pageAndPictureDefinition = firstNotSetEditablePictureDefinitionAndPageInPageList(
      pages,
      draftState.template
    );
    if (pageAndPictureDefinition) {
      if (!pageKey && tryToFillFirstEmptyPicture) {
        targetPage = pageAndPictureDefinition.page;
      }
      targetEditablePictureDefinition = pageAndPictureDefinition.editablePictureDefinition;
    }
  }

  if (pageKey) {
    const selectedPage = findPage(pageKey, draftState.content);
    if (selectedPage) {
      targetPage = selectedPage;
      if (editablePictureKey) {
        const pageDefinition = findPageDefinition(
          selectedPage.pageDefinitionKey,
          draftState.template
        );
        targetEditablePictureDefinition = findEditablePictureDefinition(
          editablePictureKey,
          pageDefinition
        );
        if (targetEditablePictureDefinition) {
          const oldPictureIndex = targetPage.editablePictures.findIndex(
            (picture) => picture.editablePictureKey === editablePictureKey
          );
          if (oldPictureIndex > -1) {
            targetPage.editablePictures.splice(oldPictureIndex, 1);
          }
        }
      } else {
        targetEditablePictureDefinition = firstNotSetEditablePictureDefinition(
          targetPage,
          draftState.template
        );
      }
      if (!targetEditablePictureDefinition) {
        targetEditablePictureDefinition = singleEditablePictureDefinition(
          targetPage,
          draftState.template
        );
        if (targetEditablePictureDefinition) {
          targetPage.editablePictures.shift();
        }
      }
    } else {
      return;
    }
  }

  if (!pageKey && (!tryToFillFirstEmptyPicture || !targetEditablePictureDefinition)) {
    if (maxPageReached(draftState.content, configuration, draftState.template.pageDefinitions)) {
      const maxPagesMessage = getMaxPageReachedMessage(
        draftState.content.templateType,
        configuration
      );
      const notification = {
        code: "KFE-003",
        customMessage: maxPagesMessage,
        key: uuid(),
      };
      addNotificationRemovingSameOnes(draftState.notifications, notification);
      return;
    }

    let newPageDefinition: TemplatePageDefinition;

    const pageDefinitionsWithEditablePicture = draftState.template.pageDefinitions.filter((def) =>
      def.elementDefinitions.find(({ type }) => type === "editable-picture")
    );
    const noCoverTemplatePageDefinitions = getNoCoverDefinitions(
      pageDefinitionsWithEditablePicture
    );

    if (
      lastPage &&
      noCoverTemplatePageDefinitions.find(
        (definition) => definition.key === lastPage.pageDefinitionKey
      )
    ) {
      newPageDefinition = findPageDefinition(lastPage.pageDefinitionKey, draftState.template);
    } else {
      newPageDefinition = noCoverTemplatePageDefinitions[0];
    }

    targetPage = generateNewPage(newPageDefinition.key, draftState.template);
    addPageMutation(targetPage, draftState.content);

    targetEditablePictureDefinition = firstNotSetEditablePictureDefinition(
      targetPage,
      draftState.template
    );

    const nbPagesToAdd = getNumberPagesToAdd(draftState.template, newPageDefinition, configuration);

    for (let i = 0; i < nbPagesToAdd - 1; i++) {
      const newPage = generateNewPage(newPageDefinition.key, draftState.template);
      addPageMutation(newPage, draftState.content);
    }
  }

  if (!targetPage || !targetEditablePictureDefinition) {
    return;
  }

  const elementSizeMm = sizeDmmToMm(targetEditablePictureDefinition);
  const nextRatio = elementSizeMm.width.value / elementSizeMm.height.value;

  const cropping = calcDefaultCropping(
    { nextRatio, selectionPhoto },
    !targetEditablePictureDefinition.shapeKey
  );

  const editablePicture: ContentEditablePicture = {
    cropping,
    editablePictureKey: targetEditablePictureDefinition.key,
    filterTag: "none",
    selectionPhotoKey: selectionPhoto.key,
  };

  addSelectionPhotoToSelection(selectionPhoto, draftState.selection);
  targetPage.editablePictures.push(editablePicture);
}
